/* eslint-disable @typescript-eslint/no-unused-vars */
import { useContext, useEffect, useState } from "react";
import { useWebStoreContext } from "contexts/WebStoreProvider";
import { IShopPurchaseOfferMultiItem } from "contexts/WebStoreProvider/types";
import { FeaturedDealsSkeleton } from "../../skeleton";
import { offersApi, useGetOffersQuery } from "redux/apis";
import { ShopContext } from "../../index";
import { FeaturedOfferSection } from "..";
import { useDispatch } from "react-redux";
import { useAppSelector, usePopupsState } from "hooks";
import {
	IShopSection,
	selectShopSectionsState
} from "redux/slices/shop-sections";
import { FeaturedOfferSectionV2 } from "../FeaturedOfferSectionV2";
import { FeaturesFlags } from "helpers";
import { useEnvContext } from "contexts";
import { SxProps } from "@mui/material";
import { ELITE_DEALS_GOLD } from "pages/EliteDeals/helpers/colors";
import { SectionSource } from "interfaces/web-store/section-source";

const FeaturedDeals = ({ from = "shop" }: { from?: SectionSource }) => {
	const dispatch = useDispatch();
	const { authToken } = useWebStoreContext();
	const { isFeatureFlagEnabled } = useEnvContext();
	const [content, setContent] = useState<JSX.Element[]>();
	const isElite = from === "elite";
	const headerSx: SxProps | undefined = isElite
		? {
				color: ELITE_DEALS_GOLD,
				"& svg": { fill: ELITE_DEALS_GOLD }
		  }
		: undefined;
	const { shopSections } = useAppSelector(selectShopSectionsState);
	const { isAnyPopupOpen } = usePopupsState();
	const { data: shop } = useGetOffersQuery(
		{
			offerType: "featured",
			isAuthenticated: !!authToken?.value
		},
		{
			pollingInterval: 600000,
			refetchOnFocus: true,
			skip: isAnyPopupOpen
		}
	);
	const { sectionMenuData } = useContext(ShopContext);
	// Compared by value: sectionMenuData is rebuilt on every featured offers response, so depending
	// on it would refetch offers in a loop if the response is never deep-equal (e.g. `now`)
	const sectionsKey = JSON.stringify(
		shopSections?.mainShopSections?.map(
			({ name, startTime, endTime }: IShopSection) => ({
				name,
				startTime,
				endTime
			})
		)
	);

	useEffect(() => {
		if (!shopSections?.mainShopSections || !shop) {
			return;
		}

		const filteredOffers = (
			(shop.offers ?? []) as IShopPurchaseOfferMultiItem[]
		).filter((offer) => {
			const offerIsElite = offer?.isElite === true;
			return isElite ? offerIsElite : !offerIsElite;
		});

		//TODO: FIX any
		/* eslint-disable */
		setContent(
			shopSections?.mainShopSections
				.filter((data) =>
					filteredOffers.find((of: any) => of.sectionName === data.name)
				)
				.map((data) => {
					const sectionOffers = filteredOffers.filter(
						(of: any) => of.sectionName === data.name
					);
					const sectionData =
						isElite && data.name === "featured"
							? { ...data, title: "Exclusive Deals" }
							: data;
					const sectionProps = {
						data: sectionData,
						sectionRef: sectionMenuData?.find(
							(item: any) => item.name === data.name
						)?.sectionRef,
						uniqueKey: data.name,
						from
					};

					return isFeatureFlagEnabled(FeaturesFlags.FEATURED_SECTION_V2) ? (
						<FeaturedOfferSectionV2
							{...sectionProps}
							key={data.name}
							sectionOffers={sectionOffers}
							headerSx={headerSx}
						/>
					) : (
						<FeaturedOfferSection
							{...sectionProps}
							key={data.name}
							sectionOffers={sectionOffers}
							headerSx={headerSx}
						/>
					);
				})
		);
		/* eslint-enable */
	}, [shop, shopSections?.mainShopSections, sectionMenuData, isElite]);

	useEffect(() => {
		dispatch(
			offersApi.util.invalidateTags([
				{
					type: "offersTag",
					id: "featured"
				}
			])
		);
	}, [sectionsKey]);

	if (!content) {
		return <FeaturedDealsSkeleton />;
	}

	return <>{content}</>;
};

export default FeaturedDeals;
